import {
  AIProvider,
  BugReportPriority,
  BugReportType,
  ControlQuestionnaireType,
  Feature,
  MeetingMinuteStatus,
  PciEvidencePackEffort,
  PciEvidencePackPriority,
  PermissionLevel,
  PolicyGenerationMethod,
  PolicyStatus,
  ReleaseNoteCategory,
  ReleaseNoteType,
  Role,
  SecurityQuestionGroupType,
  SecurityQuestionType,
  ServiceProviderAssessmentTemplateStatus,
} from '@/enum/global.enum'
import {
  AssetStatus,
  AvailabilityRating,
  BusinessImpactLevel,
  ConfidentialityRating,
  IntegrityRating,
} from '@/enum/information-asset.enum'
import type { LoginSchemaType } from '@/schema/auth/login'
import type { SignupSchemaType } from '@/schema/auth/signup'
import type { CreateBugReportSchemaType, UpdateBugReportSchemaType } from '@/schema/bug-report.schema'
import type {
  CreateFileLinkSchemaType,
  CreateFileRepositorySchemaType,
  CreateFolderSchemaType,
  UpdateFileLinkSchemaType,
  UpdateFileRepositorySchemaType,
  UpdateFolderSchemaType,
} from '@/schema/document-repo.schema'
import type {
  CreateMaturityModelSchemaType,
  UpdateMaturityModelSchemaType,
} from '@/schema/maturity/maturity-model.schema'
import type {
  CreateAIRegisterTypeSchemaType,
  UpdateAIRegisterTypeSchemaType,
} from '@/schema/organisation/ai-register/ai-register-type.schema'
import type {
  CreateAIRegisterPermittedDataSchemaType,
  UpdateAIRegisterPermittedDataSchemaType,
} from '@/schema/organisation/ai-register/permitted-data.schema'
import type {
  CreateInformationSecurityObjectiveAnalysisEvaluationFrequencySchemaType,
  UpdateInformationSecurityObjectiveAnalysisEvaluationFrequencySchemaType,
} from '@/schema/organisation/information-security-objectives/analysis-evaluation-frequency.schema'
import type {
  CreateInformationSecurityObjectiveAreaSchemaType,
  UpdateInformationSecurityObjectiveAreaSchemaType,
} from '@/schema/organisation/information-security-objectives/area.schema'
import type {
  CreateInformationSecurityObjectiveMeasureTypeSchemaType,
  UpdateInformationSecurityObjectiveMeasureTypeSchemaType,
} from '@/schema/organisation/information-security-objectives/measure-type.schema'
import type {
  CreateInformationSecurityObjectiveMonitoringFrequencySchemaType,
  UpdateInformationSecurityObjectiveMonitoringFrequencySchemaType,
} from '@/schema/organisation/information-security-objectives/monitoring-frequency.schema'
import type {
  CreateInformationSecurityObjectivePrioritySchemaType,
  UpdateInformationSecurityObjectivePrioritySchemaType,
} from '@/schema/organisation/information-security-objectives/priority.schema'
import type {
  CreateInformationSecurityObjectiveReportingFormatSchemaType,
  UpdateInformationSecurityObjectiveReportingFormatSchemaType,
} from '@/schema/organisation/information-security-objectives/reporting-format.schema'
import type {
  CreateInformationSecurityObjectiveStatusSchemaType,
  UpdateInformationSecurityObjectiveStatusSchemaType,
} from '@/schema/organisation/information-security-objectives/status.schema'

import type {
  CreateChangeManagementProcessCategorySchemaType,
  CreateChangeManagementProcessSchemaType,
  UpdateChangeManagementProcessCategorySchemaType,
  UpdateChangeManagementProcessSchemaType,
} from '@/schema/organisation/change-management-process.schema'
import type {
  CreateISMScopingIntroductionSchemaType,
  UpdateISMScopingIntroductionSchemaType,
} from '@/schema/organisation/compliance/ism/scoping/ism-scoping-introduction.schema'
import type {
  CreateRegionSchemaType,
  CreateRegulatoryCategorySchemaType,
  CreateRiskTypeSchemaType,
  UpdateRegionSchemaType,
  UpdateRegulatoryCategorySchemaType,
  UpdateRiskTypeSchemaType,
} from '@/schema/organisation/country-regulatory-register-oos.schema'
import type { CreateProgramsSchemaType, UpdateProgramsSchemaType } from '@/schema/organisation/ctra/programs.schema'
import type {
  CreateThreatScenarioCatalogSchemaType,
  UpdateThreatScenarioCatalogSchemaType,
} from '@/schema/organisation/ctra/threat-scenario-catalog.schema'
import type { CreateZoneSchemaType, UpdateZoneSchemaType } from '@/schema/organisation/ctra/zone.schema'
import type { CreateDepartmentSchemaType, UpdateDepartmentSchemaType } from '@/schema/organisation/department.schema'
import type { CreateIncidentTypeSchemaType, UpdateIncidentTypeSchemaType } from '@/schema/organisation/incident.schema'
import type {
  CreateAssetTypeSchemaType,
  UpdateAssetTypeSchemaType,
} from '@/schema/organisation/information-asset.schema'
import type {
  CreateMeetingMinutesSchemaType,
  CreateMeetingMinuteTypeSchemaType,
  GenerateMeetingMinutesUsingAISchemaType,
  UpdateMeetingMinutesSchemaType,
  UpdateMeetingMinuteTypeSchemaType,
} from '@/schema/organisation/meeting-minutes.schema'
import type { CreateN8nWebhookSchemaType, UpdateN8nWebhookSchemaType } from '@/schema/organisation/n8n-webhook.schema'
import type {
  CreateActionStatusSchemaType,
  CreateAffectedAreaSchemaType,
  CreateImportanceSchemaType,
  CreateRootIdentificationSchemaType,
  UpdateActionStatusSchemaType,
  UpdateAffectedAreaSchemaType,
  UpdateImportanceSchemaType,
  UpdateRootIdentificationSchemaType,
} from '@/schema/organisation/non-conformity.schema'
import type {
  CreateOrganisationPreferenceSchemaType,
  UpdateOrganisationPreferenceSchemaType,
} from '@/schema/organisation/organisation-preference.schema'
import type {
  CreateChildOrganisationType,
  UpdateChildOrganisationType,
} from '@/schema/organisation/organisation.schema'
import type { AssessedMerchantSchemaType } from '@/schema/organisation/pci/saq/config/assessed-merchant.schema'
import type {
  CreatePCIScopingCDESchemaType,
  UpdatePCIScopingCDESchemaType,
} from '@/schema/organisation/pci/scoping/pci-scoping-cde.schema'
import type {
  CreatePCIScopingPeopleAccessTypeSchemaType,
  CreatePCIScopingPeopleTrainingSchemaType,
  UpdatePCIScopingPeopleAccessTypeSchemaType,
  UpdatePCIScopingPeopleTrainingSchemaType,
} from '@/schema/organisation/pci/scoping/pci-scoping-people.schema'
import type {
  CreatePCIScopingProcessDataHandledSchemaType,
  CreatePCIScopingProcessFrequencySchemaType,
  UpdatePCIScopingProcessDataHandledSchemaType,
  UpdatePCIScopingProcessFrequencySchemaType,
} from '@/schema/organisation/pci/scoping/pci-scoping-process.schema'
import type {
  CreatePolicyCategorySchemaType,
  CreatePolicyReviewFrequencySchemaType,
  UpdatePolicyCategorySchemaType,
  UpdatePolicyReviewFrequencySchemaType,
} from '@/schema/organisation/policy.schema'
import type { CreateProjectSchemaType, UpdateProjectSchemaType } from '@/schema/organisation/project.schema'
import type {
  CreateRiskTreatmentDecisionSchemaType,
  UpdateRiskTreatmentDecisionSchemaType,
} from '@/schema/organisation/risk-treatment-decision.schema'
import type {
  CreateRiskCategorySchemaType,
  CreateRiskIdentificationSourceSchemaType,
  CreateRiskImpactSchemaType,
  CreateRiskLikelihoodSchemaType,
  CreateRiskOwnerSchemaType,
  CreateRiskRatingSchemaType,
  CreateThreatActorSchemaType,
  CreateThreatCategorySchemaType,
  CreateThreatSourceSchemaType,
  CreateVulnerabilitySchemaType,
  UpdateRiskCategorySchemaType,
  UpdateRiskGeneralInfoFormSchemaType,
  UpdateRiskIdentificationSourceSchemaType,
  UpdateRiskImpactSchemaType,
  UpdateRiskLikelihoodSchemaType,
  UpdateRiskOwnerSchemaType,
  UpdateRiskRatingSchemaType,
  UpdateThreatActorSchemaType,
  UpdateThreatCategorySchemaType,
  UpdateThreatSourceSchemaType,
  UpdateVulnerabilitySchemaType,
} from '@/schema/organisation/risk.schema'
import type {
  CreateAssessmentTierSchemaType,
  CreateServiceProviderAssessmentTemplateSchemaType,
  CreateServiceProviderAssuranceProfileDetailsSchemaType,
  CreateServiceProviderContractStatusSchemaType,
  CreateServiceProviderGeneralDetailsSchemaType,
  CreateServiceTypeSchemaType,
  UpdateAssessmentTierSchemaType,
  UpdateServiceProviderAssessmentTemplateSchemaType,
  UpdateServiceProviderContractStatusSchemaType,
  UpdateServiceProviderGeneralDetailsSchemaType,
  UpdateServiceTypeSchemaType,
} from '@/schema/organisation/service-provider.schema'
import type { CreateStaffSchemaType, UpdateStaffSchemaType } from '@/schema/organisation/staff.schema'
import type {
  CreateTaskSeveritySchemaType,
  CreateTaskStatusSchemaType,
  UpdateTaskSeveritySchemaType,
  UpdateTaskStatusSchemaType,
} from '@/schema/organisation/task.schema'
import type {
  CreateZapierWebhookSchemaType,
  UpdateZapierWebhookSchemaType,
} from '@/schema/organisation/zapier-webhook.schema'
import type {
  CreateSecurityQuestionGroupSchemaType,
  UpdateSecurityQuestionGroupSchemaType,
} from '@/schema/questionnaire/security-question-group.schema'
import type { CreateStandardsSchemaType, UpdateStandardsSchemaType } from '@/schema/standards.schema'
import type { CreateAdminSchemaType, UpdateAdminSchemaType } from '@/schema/superadmin/admin.schema'
import type { CreateAIModelSchemaType, UpdateAIModelSchemaType } from '@/schema/superadmin/ai-model.schema'
import type { CreateClerkUserType, UpdateClerkUserType } from '@/schema/superadmin/clerk-user.schema'
import type { CreateHelpfulTipSchemaType, UpdateHelpfulTipSchemaType } from '@/schema/superadmin/helpful-tips.schema'
import type {
  CreateOrganisationRequestType,
  UpdateOrganisationRequestType,
} from '@/schema/superadmin/organisation-manager.schema'
import type {
  CreateOrganisationUserByOrganisationAdminType,
  CreateOrganisationUserType,
  CreateOrganisationUserWithNewUserType,
  UpdateOrganisationUserType,
} from '@/schema/superadmin/organisation-user.schema'
import type { CreateOrganisationType, UpdateOrganisationType } from '@/schema/superadmin/organisation.schema'
import type {
  CreatePciDssEvidencePackTaskSchemaType,
  UpdatePciDssEvidencePackTaskSchemaType,
} from '@/schema/superadmin/pci-dss-evidence-pack-task.schema'
import type { CreatePciDssTaskSchemaType, UpdatePciDssTaskSchemaType } from '@/schema/superadmin/pci-dss-task.schema'
import type { CreateReleaseNoteSchemaType, UpdateReleaseNoteSchemaType } from '@/schema/superadmin/release-note.schema'
import type {
  CreateAssessmentTypeSchemaType,
  UpdateAssessmentTypeSchemaType,
} from '@/schema/superadmin/risk-assessment/assessment-type.schema'
import type {
  CreateDataClassificationSchemaType,
  UpdateDataClassificationSchemaType,
} from '@/schema/superadmin/risk-assessment/data-classification.schema'
import type {
  CreateHostingModelSchemaType,
  UpdateHostingModelSchemaType,
} from '@/schema/superadmin/risk-assessment/hosting-model.schema'
import type {
  CreateQuestionnaireSchemaType,
  UpdateQuestionnaireSchemaType,
} from '@/schema/superadmin/risk-assessment/questionnaire.schema'
import type {
  CreateSystemExposureSchemaType,
  UpdateSystemExposureSchemaType,
} from '@/schema/superadmin/risk-assessment/system-exposure.schema'
import type {
  CreateUserBaseSchemaType,
  UpdateUserBaseSchemaType,
} from '@/schema/superadmin/risk-assessment/user-base.schema'
import type {
  CreateWebPushNotificationSchemaType,
  UpdateWebPushNotificationSchemaType,
} from '@/schema/superadmin/web-push-notification.schema'
import { faker } from '@faker-js/faker'

export const logFakeData = (): LoginSchemaType => {
  return {
    email: faker.internet.email(),
    password: generateValidPassword(),
  }
}

export const signupFakeData = (): SignupSchemaType => {
  return {
    email: faker.internet.email(),
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    companyName: faker.company.name(),
    phone: faker.phone.number(),
  }
}

// Common admin features (admin-specific features only)
const ADMIN_FEATURES = [
  Feature.ADMIN_STANDARDS,
  Feature.ADMIN_CONTROLS,
  Feature.ADMIN_MATURITY_MODELS,
  Feature.ADMIN_SECURITY_QUESTION_GROUPS,
  Feature.ADMIN_SECURITY_QUESTIONNAIRES,
  Feature.ADMIN_ORGANISATION,
  Feature.ADMIN_ADMIN_MANAGE,
  Feature.ADMIN_RESOURCE,
  Feature.ADMIN_AI_MODEL,
  Feature.ADMIN_IT_PRIVILEGED_OPERATIONS,
  Feature.ADMIN_RISK_ASSESSMENT,
  Feature.ADMIN_CONTACT_MESSAGE,
  Feature.ADMIN_EMAIL_CUSTOMIZATION,
] as const

// Helper function to generate random feature permissions
const generateFeaturePermissions = (minCount: number = 2, maxCount: number = 6) => {
  const numPermissions = faker.number.int({ min: minCount, max: maxCount })

  // Shuffle the features array to get unique random features
  const shuffledFeatures = faker.helpers.shuffle([...ADMIN_FEATURES])

  return Array.from({ length: Math.min(numPermissions, shuffledFeatures.length) }, (_, index) => ({
    feature: shuffledFeatures[index],
    permission: faker.helpers.arrayElement(Object.values(PermissionLevel)),
  }))
}

// Helper function to generate base admin data
const generateBaseAdminData = () => ({
  firstName: faker.person.firstName(),
  lastName: faker.person.lastName(),
  role: Role.ADMIN,
  featurePermissions: generateFeaturePermissions(),
})

export const adminFakeData = (): CreateAdminSchemaType => {
  const baseData = generateBaseAdminData()
  const password = generateValidPassword()

  return {
    ...baseData,
    email: faker.internet.email(),
    password,
    confirmPassword: password,
  }
}

export const updateAdminFakeData = (): UpdateAdminSchemaType => {
  const baseData = generateBaseAdminData()

  return {
    ...baseData,
    id: faker.string.uuid(),
  }
}

// Maturity Model Fake Data
export const maturityModelFakeData = (): CreateMaturityModelSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: faker.datatype.boolean(),
  }
}

export const updateMaturityModelFakeData = (): UpdateMaturityModelSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: faker.datatype.boolean(),
  }
}

// Maturity Model Fake Data
export const standardsFakeData = (): CreateStandardsSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    logo: '',
    active: faker.datatype.boolean(),
    maturityModelId: '',
  }
}

export const updateStandardsFakeData = (): UpdateStandardsSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    logo: '',
    fallback: 'CA',
    type: faker.lorem.words(5),
    active: faker.datatype.boolean(),
    maturityModelId: '',
  }
}

export const organisationFakeData = (): CreateOrganisationType | UpdateOrganisationType => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    shortName: faker.company.name().slice(0, 10),
    description: faker.company.catchPhrase(),
    logo: faker.image.url(),
    active: true,
    maxUsers: faker.number.int({ min: 10, max: 1000 }),
  }
}

export const childOrganisationFakeData = (): CreateChildOrganisationType | UpdateChildOrganisationType => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    shortName: faker.company.name().slice(0, 10),
    description: faker.company.catchPhrase(),
    logo: faker.image.url(),
  }
}

export const organisationUserFakeData = (): CreateOrganisationUserType | UpdateOrganisationUserType => {
  const password = generateValidPassword()

  return {
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    email: faker.internet.email(),
    password,
  }
}

export const clerkUserFakeData = (): CreateClerkUserType | UpdateClerkUserType => {
  const password = generateValidPassword()

  return {
    emailAddress: [faker.internet.email()],
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    password,
  }
}

// Common admin features (admin-specific features only)
export const ORG_FEATURES = [
  Feature.USERS,
  Feature.POLICY,
  Feature.PROJECT,
  Feature.MEETING_MINUTES,
  Feature.ZAPIER,
  Feature.PHISHED,
  Feature.AZURE,
  Feature.CONFLUENCE,
  Feature.N8N,
  Feature.AI_REGISTER,
  Feature.SERVICE_PROVIDER,
  Feature.DOCUMENT_REPOSITORY,
  Feature.RISK,
  Feature.INFORMATION_ASSET,
  Feature.RISK_ASSESSMENT,
  Feature.INCIDENT_REGISTER,
  Feature.ASSESSMENTS,
  Feature.REPORT_LIBRARY,
  Feature.SUB_ORGANISATION,
  Feature.COUNTRY_REGULATORY_REGISTER,
  Feature.EXEMPTIONS_REGISTER,
  Feature.CHANGE_MANAGEMENT_PROCESS,
  Feature.RUN_ZERO_ASSET,
  Feature.PALO_ALTO,
  Feature.MS_DEFENDER,
  Feature.VANTA,
  Feature.DRATA,
  Feature.MIMECAST,
] as const

// Helper function to generate random feature permissions
export const generateOrganisationFeaturePermissions = (minCount: number = 2, maxCount: number = 6) => {
  const numPermissions = faker.number.int({ min: minCount, max: maxCount })

  // Shuffle the features array to get unique random features
  const shuffledFeatures = faker.helpers.shuffle([...ORG_FEATURES])

  return Array.from({ length: Math.min(numPermissions, shuffledFeatures.length) }, (_, index) => ({
    feature: shuffledFeatures[index],
    permission: faker.helpers.arrayElement(Object.values(PermissionLevel)),
  }))
}

export const organisationUserWithNewUserFakeData = (): CreateOrganisationUserWithNewUserType => {
  const password = generateValidPassword()

  return {
    organisationId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    role: faker.helpers.arrayElement([Role.ORG_ADMIN, Role.ORG_MANAGER]),
    email: faker.internet.email(),
    password,
    confirmPassword: password,
    featurePermissions: generateOrganisationFeaturePermissions(),
  }
}

export const createOrgUserFakeData = (): CreateOrganisationUserByOrganisationAdminType => {
  const password = generateValidPassword()

  return {
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    role: faker.helpers.arrayElement([Role.ORG_ADMIN, Role.ORG_MANAGER]),
    email: faker.internet.email(),
    password,
    confirmPassword: password,
    featurePermissions: generateOrganisationFeaturePermissions(),
  }
}

export const createWebPushNotificationFakeData = (): CreateWebPushNotificationSchemaType => {
  return {
    title: faker.lorem.words(5),
    userId: '',
    body: faker.lorem.sentence(15),
  }
}

export const updateWebPushNotificationFakeData = (): UpdateWebPushNotificationSchemaType => {
  return {
    id: faker.string.uuid(),
    title: faker.lorem.words(5),
    userId: '',
    body: faker.lorem.sentence(15),
  }
}

export const fileRepositoryFakeData = (): CreateFileRepositorySchemaType => {
  return {
    name: faker.company.name(),
    description: faker.lorem.sentence(),
  }
}
export const updateFileRepositoryFakeData = (): UpdateFileRepositorySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    description: faker.lorem.sentence(),
  }
}

export const folderFakeData = (): CreateFolderSchemaType => {
  return {
    name: faker.company.name(),
    repositoryId: faker.string.uuid(),
  }
}

export const updateFolderFakeData = (): UpdateFolderSchemaType => {
  return {
    name: faker.company.name(),
    id: faker.string.uuid(),
  }
}

export const linkFakeData = (): CreateFileLinkSchemaType => {
  return {
    repositoryId: faker.string.uuid(),
    name: faker.company.name(),
    url: faker.internet.url(),
  }
}

export const updateLinkFakeData = (): UpdateFileLinkSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    url: faker.internet.url(),
  }
}

export const controlFakeData = () => {
  return {
    controlNumber: faker.lorem.words(3),
    name: faker.lorem.words(3),
    sequence: faker.number.int({ min: 1, max: 5 }),
    active: faker.datatype.boolean(),
    description: faker.lorem.sentence(),
  }
}

export const updateControlFake = () => {
  return {
    id: faker.string.uuid(),
    controlNumber: faker.lorem.words(3),
    name: faker.lorem.words(3),
    sequence: faker.number.int({ min: 1, max: 5 }),
    active: faker.datatype.boolean(),
    description: faker.lorem.sentence(),
  }
}

export const securityQuestionGroupFakeData = (): CreateSecurityQuestionGroupSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    sequence: faker.number.int({ min: 1, max: 5 }),
    type: faker.helpers.arrayElement(Object.values(SecurityQuestionGroupType)),
  }
}

export const updateSecurityQuestionGroupFakeData = (): UpdateSecurityQuestionGroupSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    sequence: faker.number.int({ min: 1, max: 5 }),
    type: faker.helpers.arrayElement(Object.values(SecurityQuestionGroupType)),
  }
}

export const securityQuestionFakeData = () => {
  return {
    question: faker.lorem.sentence(),
    questionNumber: faker.lorem.words(3),
    sequence: faker.number.int({ min: 1, max: 5 }),
    active: faker.datatype.boolean(),
    type: faker.helpers.arrayElement(Object.values(SecurityQuestionType)),
  }
}

export const updateSecurityQuestionFakeData = () => {
  return {
    id: faker.string.uuid(),
    question: faker.lorem.sentence(),
    questionNumber: faker.lorem.words(3),
    sequence: faker.number.int({ min: 1, max: 5 }),
    active: faker.datatype.boolean(),
    type: faker.helpers.arrayElement(Object.values(SecurityQuestionType)),
  }
}

export const controlQuestionnaireFakeData = () => {
  return {
    question: faker.lorem.sentence(),
    expectedAnswer: faker.lorem.words(3),
    sequence: faker.number.int({ min: 1, max: 5 }),
    active: faker.datatype.boolean(),
    type: faker.helpers.arrayElement(Object.values(ControlQuestionnaireType)),
    isMultiple: faker.datatype.boolean(),
    options: faker.lorem.words(5),
    controlId: '',
    standardId: '',
    controlGroupId: '',
  }
}

export const updateControlQuestionnaireFakeData = () => {
  return {
    id: faker.string.uuid(),
    question: faker.lorem.sentence(),
    expectedAnswer: faker.lorem.words(3),
    sequence: faker.number.int({ min: 1, max: 5 }),
    active: faker.datatype.boolean(),
    type: faker.helpers.arrayElement(Object.values(ControlQuestionnaireType)),
    isMultiple: faker.datatype.boolean(),
    options: faker.lorem.words(5),
  }
}

export const createOrganisationRequestFakeData = (): CreateOrganisationRequestType => {
  return {
    name: faker.company.name(),
    description: faker.lorem.sentence(),
  }
}

export const updateOrganisationRequestFakeData = (): UpdateOrganisationRequestType => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    description: faker.lorem.sentence(),
  }
}

export const createPolicyCategoryFakeData = (): CreatePolicyCategorySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    departmentIds: [],
    active: true,
  }
}

export const updatePolicyCategoryFakeData = (): UpdatePolicyCategorySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    departmentIds: [],
    active: true,
  }
}

export const createPolicyReviewFrequencyFakeData = (): CreatePolicyReviewFrequencySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
    reminderDaysBefore: [30, 15, 7],
  }
}

export const createDepartmentFakeData = (): CreateDepartmentSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateDepartmentFakeData = (): UpdateDepartmentSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createRiskTreatmentDecisionFakeData = (): CreateRiskTreatmentDecisionSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateRiskTreatmentDecisionFakeData = (): UpdateRiskTreatmentDecisionSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createAIRegisterPermittedDataFakeData = (): CreateAIRegisterPermittedDataSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateAIRegisterPermittedDataFakeData = (): UpdateAIRegisterPermittedDataSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createAIRegisterTypeFakeData = (): CreateAIRegisterTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateAIRegisterTypeFakeData = (): UpdateAIRegisterTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createInformationSecurityObjectiveStatusFakeData =
  (): CreateInformationSecurityObjectiveStatusSchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      colour: faker.color.rgb(),
      active: true,
    }
  }

export const updateInformationSecurityObjectiveStatusFakeData =
  (): UpdateInformationSecurityObjectiveStatusSchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      colour: faker.color.rgb(),
      active: true,
    }
  }

export const createInformationSecurityObjectivePriorityFakeData =
  (): CreateInformationSecurityObjectivePrioritySchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      colour: faker.color.rgb(),
      active: true,
    }
  }

export const updateInformationSecurityObjectivePriorityFakeData =
  (): UpdateInformationSecurityObjectivePrioritySchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      colour: faker.color.rgb(),
      active: true,
    }
  }

export const createInformationSecurityObjectiveReportingFormatFakeData =
  (): CreateInformationSecurityObjectiveReportingFormatSchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const updateInformationSecurityObjectiveReportingFormatFakeData =
  (): UpdateInformationSecurityObjectiveReportingFormatSchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const createInformationSecurityObjectiveAnalysisEvaluationFrequencyFakeData =
  (): CreateInformationSecurityObjectiveAnalysisEvaluationFrequencySchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const updateInformationSecurityObjectiveAnalysisEvaluationFrequencyFakeData =
  (): UpdateInformationSecurityObjectiveAnalysisEvaluationFrequencySchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const createInformationSecurityObjectiveMonitoringFrequencyFakeData =
  (): CreateInformationSecurityObjectiveMonitoringFrequencySchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const updateInformationSecurityObjectiveMonitoringFrequencyFakeData =
  (): UpdateInformationSecurityObjectiveMonitoringFrequencySchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const createInformationSecurityObjectiveMeasureTypeFakeData =
  (): CreateInformationSecurityObjectiveMeasureTypeSchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const updateInformationSecurityObjectiveMeasureTypeFakeData =
  (): UpdateInformationSecurityObjectiveMeasureTypeSchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      active: true,
    }
  }

export const createInformationSecurityObjectiveAreaFakeData = (): CreateInformationSecurityObjectiveAreaSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateInformationSecurityObjectiveAreaFakeData = (): UpdateInformationSecurityObjectiveAreaSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createMeetingMinutesTypeFakeData = (): CreateMeetingMinuteTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
    departmentIds: [],
  }
}

export const updateMeetingMinutesTypeFakeData = (): UpdateMeetingMinuteTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
    departmentIds: [],
  }
}

export const createMeetingMinutesFakeData = (): CreateMeetingMinutesSchemaType => {
  return {
    title: faker.lorem.words(3),
    typeId: '',
    status: faker.helpers.arrayElement(Object.values(MeetingMinuteStatus)),
    dateAndTime: faker.date.past().toISOString(),
    locationOrPlatform: faker.lorem.sentence(),
    link: faker.internet.url(),
    attendeeIds: [],
    agenda: '',
    notes: '',
  }
}

export const updateMeetingMinutesFakeData = (): UpdateMeetingMinutesSchemaType => {
  return {
    id: faker.string.uuid(),
    title: faker.lorem.words(3),
    typeId: '',
    status: faker.helpers.arrayElement(Object.values(MeetingMinuteStatus)),
    dateAndTime: faker.date.past().toISOString(),
    locationOrPlatform: faker.lorem.sentence(),
    link: faker.internet.url(),
    attendeeIds: [],
    agenda: '',
    notes: '',
  }
}

export const createChangeManagementProcessCategoryFakeData = (): CreateChangeManagementProcessCategorySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateChangeManagementProcessCategoryFakeData = (): UpdateChangeManagementProcessCategorySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updatePolicyReviewFrequencyFakeData = (): UpdatePolicyReviewFrequencySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
    reminderDaysBefore: [7, 1],
  }
}

export const createOrganisationPreferenceFakeData = (): CreateOrganisationPreferenceSchemaType => {
  return {
    key: faker.lorem.words(3),
    value: faker.lorem.sentence(),
  }
}

export const updateOrganisationPreferenceFakeData = (): UpdateOrganisationPreferenceSchemaType => {
  return {
    id: faker.string.uuid(),
    key: faker.lorem.words(3),
    value: faker.lorem.sentence(),
  }
}

export const createServiceProviderContractStatusFakeData = (): CreateServiceProviderContractStatusSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateServiceProviderContractStatusFakeData = (): UpdateServiceProviderContractStatusSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createRiskCategoryFakeData = (): CreateRiskCategorySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateRiskCategoryFakeData = (): UpdateRiskCategorySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createServiceTypeFakeData = (): CreateServiceTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createServiceProviderGeneralDetailsFakeData = (): CreateServiceProviderGeneralDetailsSchemaType => ({
  name: faker.company.name(),
  email: 'prawesh.poudel7@gmail.com',
  serviceTypeId: '',
  description: faker.lorem.paragraph(),
  pointOfContact: faker.person.fullName(),
})

export const updateServiceProviderGeneralDetailsFakeData = (): UpdateServiceProviderGeneralDetailsSchemaType => ({
  id: faker.string.uuid(),
  ...createServiceProviderGeneralDetailsFakeData(),
})

export const createServiceProviderAssuranceProfileDetailsFakeData =
  (): CreateServiceProviderAssuranceProfileDetailsSchemaType => {
    const contractStartDate = faker.date.past({ years: 1 })
    const contractEndDate = faker.date.future({ years: 7, refDate: contractStartDate })
    const lastReviewDate = faker.date.recent({ days: 30 })
    const nextReviewDate = faker.date.soon({ days: 90, refDate: lastReviewDate })
    const formatDate = (date: Date) => {
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    }

    return {
      id: faker.string.uuid(),
      contractStatusId: '',
      contractStartDate: formatDate(contractStartDate),
      contractEndDate: formatDate(contractEndDate),
      assessmentTierCriticalityId: '',
      assessmentTierSensitivityId: '',
      riskRatingId: '',
      dataShared: faker.lorem.paragraph(),
      lastReviewDate: formatDate(lastReviewDate),
      nextReviewDate: formatDate(nextReviewDate),
    }
  }

export const updateServiceTypeFakeData = (): UpdateServiceTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}
export const createAssessmentTierFakeData = (): CreateAssessmentTierSchemaType => {
  return {
    name: faker.lorem.words(3),
    color: faker.color.rgb(),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateAssessmentTierFakeData = (): UpdateAssessmentTierSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    color: faker.color.rgb(),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createIncidentTypeFakeData = (): CreateIncidentTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateIncidentTypeFakeData = (): UpdateIncidentTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createVulnerabilityFakeData = (): CreateVulnerabilitySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    dateIdentified: faker.date.past().toISOString(),
    riskIds: [],
    active: true,
  }
}

export const updateVulnerabilityFakeData = (): UpdateVulnerabilitySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    dateIdentified: faker.date.past().toISOString(),
    active: true,
    riskIds: [],
  }
}

export const createThreatCategoryFakeData = (): CreateThreatCategorySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateThreatCategoryFakeData = (): UpdateThreatCategorySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createThreatSourceFakeData = (): CreateThreatSourceSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateThreatSourceFakeData = (): UpdateThreatSourceSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createThreatActorFakeData = (): CreateThreatActorSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateThreatActorFakeData = (): UpdateThreatActorSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createThreatFakeData = () => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
    threatCategoryId: '',
    threatSourceId: '',
    threatActorId: '',
  }
}

export const updateThreatFakeData = () => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    threatCategoryId: '',
    threatSourceId: '',
    threatActorId: '',
    active: true,
  }
}

export const createRiskLikelihoodFakeData = (): CreateRiskLikelihoodSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    value: faker.number.int({ min: 0, max: 10 }),
  }
}

export const updateRiskLikelihoodFakeData = (): UpdateRiskLikelihoodSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    value: faker.number.int({ min: 0, max: 10 }),
  }
}

export const createRiskImpactFakeData = (): CreateRiskImpactSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    value: faker.number.int({ min: 0, max: 10 }),
  }
}

export const updateRiskImpactFakeData = (): UpdateRiskImpactSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    value: faker.number.int({ min: 0, max: 10 }),
  }
}

export const createRiskRatingFakeData = (): CreateRiskRatingSchemaType => {
  return {
    name: faker.lorem.words(3),
    color: faker.color.rgb(),
    description: faker.lorem.sentence(),
    value: faker.number.int({ min: 0, max: 10 }),
  }
}

export const updateRiskRatingFakeData = (): UpdateRiskRatingSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    color: faker.color.rgb(),
    description: faker.lorem.sentence(),
    value: faker.number.int({ min: 0, max: 10 }),
  }
}

export const createRiskOwnerFakeData = (): CreateRiskOwnerSchemaType => {
  return {
    owner: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateRiskOwnerFakeData = (): UpdateRiskOwnerSchemaType => {
  return {
    id: faker.string.uuid(),
    owner: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createRiskIdentificationSourceFakeData = (): CreateRiskIdentificationSourceSchemaType => {
  return {
    source: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateRiskIdentificationSourceFakeData = (): UpdateRiskIdentificationSourceSchemaType => {
  return {
    id: faker.string.uuid(),
    source: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createTaskStatusFakeData = (): CreateTaskStatusSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateTaskStatusFakeData = (): UpdateTaskStatusSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createTaskSeverityFakeData = (): CreateTaskSeveritySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateTaskSeverityFakeData = (): UpdateTaskSeveritySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createActionStatusFakeData = (): CreateActionStatusSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateActionStatusFakeData = (): UpdateActionStatusSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createImportanceFakeData = (): CreateImportanceSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateImportanceFakeData = (): UpdateImportanceSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createAffectedAreaFakeData = (): CreateAffectedAreaSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateAffectedAreaFakeData = (): UpdateAffectedAreaSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createRootIdentificationFakeData = (): CreateRootIdentificationSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateRootIdentificationFakeData = (): UpdateRootIdentificationSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createStaffFakeData = (): CreateStaffSchemaType => {
  const mobile = `04${faker.number.int({ min: 10000000, max: 99999999 })}`

  return {
    name: faker.person.fullName(),
    email: faker.internet.email(),
    phone: mobile,
    departmentId: '',
    roleAndResponsibilities: faker.lorem.sentence(),
    position: faker.lorem.words(3),
    active: true,
  }
}

export const updateStaffFakeData = (): UpdateStaffSchemaType => {
  const mobile = `04${faker.number.int({ min: 10000000, max: 99999999 })}`

  return {
    id: faker.string.uuid(),
    name: faker.person.fullName(),
    email: faker.internet.email(),
    departmentId: '',
    phone: mobile,
    roleAndResponsibilities: faker.lorem.sentence(),
    position: faker.lorem.words(3),
    active: true,
  }
}

export const createAssetTypeFakeData = (): CreateAssetTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateAssetTypeFakeData = (): UpdateAssetTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createInformationAssetFakeData = () => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    assetNumber: faker.lorem.words(3),
    ownerId: '',
    assignedToId: '',
    departmentId: '',
    code: faker.lorem.words(3),
    purchaseDate: faker.date.past().toISOString(),
    purchasePrice: faker.number.int({ min: 1000, max: 1000000 }).toString(),
    assetTypeId: '',
    confidentialityRating: faker.helpers.arrayElement(Object.values(ConfidentialityRating)),
    integrityRating: faker.helpers.arrayElement(Object.values(IntegrityRating)),
    availabilityRating: faker.helpers.arrayElement(Object.values(AvailabilityRating)),
    businessImpactLevel: faker.helpers.arrayElement(Object.values(BusinessImpactLevel)),
    assetStatus: faker.helpers.arrayElement(Object.values(AssetStatus)),
    acquisitionDate: faker.date.past().toISOString(),
    lastRiskAssessmentDate: faker.date.past().toISOString(),
    classificationLevel: faker.number.int({ min: 1, max: 5 }).toString(),
    retentionPeriod: faker.number.int({ min: 1, max: 5 }).toString(),
    systemExposureId: '',
    hostingModelId: '',
    dataClassificationId: '',
    userBaseId: '',
    documentLink: faker.internet.url(),
  }
}

export const updateInformationAssetFakeData = () => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    ownerId: '',
    assignedToId: '',
    departmentId: '',
    assetTypeId: '',
    purchaseDate: faker.date.past().toISOString(),
    purchasePrice: faker.number.int({ min: 1000, max: 1000000 }).toString(),
    code: faker.lorem.words(3),
    assetNumber: faker.lorem.words(3),
    confidentialityRating: faker.helpers.arrayElement(Object.values(ConfidentialityRating)),
    integrityRating: faker.helpers.arrayElement(Object.values(IntegrityRating)),
    availabilityRating: faker.helpers.arrayElement(Object.values(AvailabilityRating)),
    businessImpactLevel: faker.helpers.arrayElement(Object.values(BusinessImpactLevel)),
    assetStatus: faker.helpers.arrayElement(Object.values(AssetStatus)),
    acquisitionDate: faker.date.past().toISOString(),
    lastRiskAssessmentDate: faker.date.past().toISOString(),
    classificationLevel: faker.number.int({ min: 1, max: 5 }).toString(),
    retentionPeriod: faker.number.int({ min: 1, max: 5 }).toString(),
    systemExposureId: '',
    hostingModelId: '',
    dataClassificationId: '',
    userBaseId: '',
    documentLink: faker.internet.url(),
  }
}

export const createProjectFakeData = (): CreateProjectSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
  }
}

export const updateProjectFakeData = (): UpdateProjectSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
  }
}

export const createPolicyFakeData = () => {
  const generationMethod = faker.helpers.arrayElement(Object.values(PolicyGenerationMethod))
  let sourceUrl = ''
  let aiPrompt = ''
  let sourceFileId = ''
  let description = ''

  switch (generationMethod) {
    case PolicyGenerationMethod.AI:
      aiPrompt = faker.lorem.paragraph()
      break
    case PolicyGenerationMethod.URL:
      sourceUrl = faker.internet.url()
      break
    case PolicyGenerationMethod.FILE:
      sourceFileId = faker.system.fileName()
      break
    case PolicyGenerationMethod.MANUAL:
      description = faker.lorem.paragraph()
      break
  }

  return {
    name: faker.lorem.words(3),
    description,
    policyCategoryId: '',
    reviewerIds: [],
    ownerId: '',
    approverId: '',
    status: faker.helpers.arrayElement(Object.values(PolicyStatus)),
    generationMethod,
    sourceUrl,
    aiPrompt,
    sourceFileId,
  }
}

export const updatePolicyFakeData = () => {
  const generationMethod = faker.helpers.arrayElement(Object.values(PolicyGenerationMethod))
  let sourceUrl = ''
  let aiPrompt = ''
  let sourceFileId = ''
  let description = ''

  switch (generationMethod) {
    case PolicyGenerationMethod.AI:
      aiPrompt = faker.lorem.paragraph()
      break
    case PolicyGenerationMethod.URL:
      sourceUrl = faker.internet.url()
      break
    case PolicyGenerationMethod.FILE:
      sourceFileId = faker.system.fileName()
      break
    case PolicyGenerationMethod.MANUAL:
      description = faker.lorem.paragraph()
      break
  }

  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description,
    policyCategoryId: '',
    reviewerIds: [],
    ownerId: '',
    approverId: '',
    status: faker.helpers.arrayElement(Object.values(PolicyStatus)),
    generationMethod,
    sourceUrl,
    aiPrompt,
    sourceFileId,
  }
}

export const updateRiskGeneralInfoFakeData = (): UpdateRiskGeneralInfoFormSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    riskCategoryId: '',
    riskOwnerStaffId: '',
    threatIds: [],
    vulnerabilityIds: [],
    informationAssetIds: [],
    riskIdentificationSourceId: '',
    nextReviewDate: faker.date.past().toISOString(),
    description: faker.lorem.sentence(),
  }
}

export const createRiskTypeFakeData = (): CreateRiskTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    active: true,
  }
}

export const updateRiskTypeFakeData = (): UpdateRiskTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    active: true,
  }
}

export const createRegionFakeData = (): CreateRegionSchemaType => {
  return {
    name: faker.lorem.words(3),
    active: true,
  }
}

export const updateRegionFakeData = (): UpdateRegionSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    active: true,
  }
}

export const createRegulatoryCategoryFakeData = (): CreateRegulatoryCategorySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateRegulatoryCategoryFakeData = (): UpdateRegulatoryCategorySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createChangeManagementProcessFakeData = (): CreateChangeManagementProcessSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    requesterStaffId: '',
    reviewerIds: [],
    approverIds: [],
    categoryId: '',
    impactAndRiskAssessment: faker.lorem.sentence(),
    implementationPlan: faker.lorem.sentence(),
    testingAndValidation: faker.lorem.sentence(),
  }
}

export const updateChangeManagementProcessFakeData = (): UpdateChangeManagementProcessSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    requesterStaffId: '',
    reviewerIds: [],
    approverIds: [],
    categoryId: '',
    implementationPlan: faker.lorem.sentence(),
    testingAndValidation: faker.lorem.sentence(),
    impactAndRiskAssessment: faker.lorem.sentence(),
  }
}

export const createServiceProviderAssessmentTemplateFakeData =
  (): CreateServiceProviderAssessmentTemplateSchemaType => {
    return {
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      status: faker.helpers.arrayElement(Object.values(ServiceProviderAssessmentTemplateStatus)),
    }
  }

export const updateServiceProviderAssessmentTemplateFakeData =
  (): UpdateServiceProviderAssessmentTemplateSchemaType => {
    return {
      id: faker.string.uuid(),
      name: faker.lorem.words(3),
      description: faker.lorem.sentence(),
      status: faker.helpers.arrayElement(Object.values(ServiceProviderAssessmentTemplateStatus)),
    }
  }

export const createHelpfulTipFakeData = (): CreateHelpfulTipSchemaType => {
  return {
    title: faker.lorem.words(3),
    topic: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: faker.datatype.boolean(),
  }
}

export const updateHelpfulTipFakeData = (): UpdateHelpfulTipSchemaType => {
  return {
    id: faker.string.uuid(),
    title: faker.lorem.words(3),
    topic: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: faker.datatype.boolean(),
    sequence: faker.number.int({ min: 1, max: 100 }),
  }
}

export const createReleaseNoteFakeData = (): CreateReleaseNoteSchemaType => {
  return {
    version: faker.lorem.words(3),
    title: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    type: faker.helpers.arrayElement(Object.values(ReleaseNoteType)),
    category: faker.helpers.arrayElement(Object.values(ReleaseNoteCategory)),
    releaseDate: faker.date.past().toISOString(),
    isPublished: false,
  }
}

export const updateReleaseNoteFakeData = (): UpdateReleaseNoteSchemaType => {
  return {
    id: faker.string.uuid(),
    version: faker.lorem.words(3),
    title: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    type: faker.helpers.arrayElement(Object.values(ReleaseNoteType)),
    category: faker.helpers.arrayElement(Object.values(ReleaseNoteCategory)),
    releaseDate: faker.date.past().toISOString(),
    isPublished: faker.datatype.boolean(),
  }
}

export const createBugReportFakeData = (): CreateBugReportSchemaType => {
  return {
    title: faker.lorem.words(3),
    description: faker.lorem.sentence(50),
    type: faker.helpers.arrayElement(Object.values(BugReportType)),
    priority: faker.helpers.arrayElement(Object.values(BugReportPriority)),
  }
}

export const updateBugReportFakeData = (): UpdateBugReportSchemaType => {
  return {
    id: faker.string.uuid(),
    title: faker.lorem.words(3),
    description: faker.lorem.sentence(50),
    type: faker.helpers.arrayElement(Object.values(BugReportType)),
    priority: faker.helpers.arrayElement(Object.values(BugReportPriority)),
  }
}

export const createZapierWebhookFakeData = (): CreateZapierWebhookSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    webhookUrl: faker.internet.url(),
    eventTypes: [faker.lorem.word()],
    isActive: true,
  }
}

export const updateZapierWebhookFakeData = (): UpdateZapierWebhookSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    webhookUrl: faker.internet.url(),
    eventTypes: [faker.lorem.word()],
    isActive: true,
  }
}

export const createN8nWebhookFakeData = (): CreateN8nWebhookSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    webhookUrl: faker.internet.url(),
    eventTypes: [faker.lorem.word()],
    isActive: true,
  }
}

export const updateN8nWebhookFakeData = (): UpdateN8nWebhookSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    webhookUrl: faker.internet.url(),
    eventTypes: [faker.lorem.word()],
    isActive: true,
  }
}

export const createUserBaseFakeData = (): CreateUserBaseSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const updateUserBaseFakeData = (): UpdateUserBaseSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const createAssessmentTypeFakeData = (): CreateAssessmentTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const updateAssessmentTypeFakeData = (): UpdateAssessmentTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const createQuestionnaireFakeData = (): CreateQuestionnaireSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    questionnaireKey: faker.string.alphanumeric(10).toLowerCase(),
    isActive: true,
    riskAssessmentTypeId: faker.string.uuid(),
  }
}

export const updateQuestionnaireFakeData = (): UpdateQuestionnaireSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    questionnaireKey: faker.string.alphanumeric(10).toLowerCase(),
    isActive: true,
    riskAssessmentTypeId: faker.string.uuid(),
  }
}

export const createDataClassificationFakeData = (): CreateDataClassificationSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    weight: faker.number.int({ min: 1, max: 5 }),
    isActive: true,
  }
}

export const updateDataClassificationFakeData = (): UpdateDataClassificationSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    weight: faker.number.int({ min: 1, max: 5 }),
    isActive: true,
  }
}

export const createHostingModelFakeData = (): CreateHostingModelSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const updateHostingModelFakeData = (): UpdateHostingModelSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const createSystemExposureFakeData = (): CreateSystemExposureSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const updateSystemExposureFakeData = (): UpdateSystemExposureSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    isActive: true,
  }
}

export const createAIModelFakeData = (): CreateAIModelSchemaType => {
  return {
    name: faker.lorem.words(3),
    modelName: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    provider: faker.helpers.arrayElement(Object.values(AIProvider)),
    isActive: true,
  }
}

export const updateAIModelFakeData = (): UpdateAIModelSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    modelName: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    provider: faker.helpers.arrayElement(Object.values(AIProvider)),
    isActive: true,
  }
}

export const createPCIScopingCDEFakeData = (): CreatePCIScopingCDESchemaType => {
  return {
    description: faker.lorem.sentence(20),
    isPANInScope: faker.datatype.boolean(),
    isSADInScope: faker.datatype.boolean(),
  }
}

export const updatePCIScopingCDEFakeData = (): UpdatePCIScopingCDESchemaType => {
  return {
    id: faker.string.uuid(),
    description: faker.lorem.sentence(20),
    isPANInScope: faker.datatype.boolean(),
    isSADInScope: faker.datatype.boolean(),
  }
}

export const createPCIScopingPeopleAccessTypeFakeData = (): CreatePCIScopingPeopleAccessTypeSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updatePCIScopingPeopleAccessTypeFakeData = (): UpdatePCIScopingPeopleAccessTypeSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createPCIScopingPeopleTrainingFakeData = (): CreatePCIScopingPeopleTrainingSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updatePCIScopingPeopleTrainingFakeData = (): UpdatePCIScopingPeopleTrainingSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createPCIScopingProcessDataHandledFakeData = (): CreatePCIScopingProcessDataHandledSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updatePCIScopingProcessDataHandledFakeData = (): UpdatePCIScopingProcessDataHandledSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createPCIScopingProcessFrequencyFakeData = (): CreatePCIScopingProcessFrequencySchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updatePCIScopingProcessFrequencyFakeData = (): UpdatePCIScopingProcessFrequencySchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createISMScopingIntroductionFakeData = (): CreateISMScopingIntroductionSchemaType => {
  return {
    assessmentBoundary: faker.lorem.sentence(20),
    dataClassification: faker.helpers.arrayElement(['NC', 'OFFICIAL_SENSITIVE', 'PROTECTED', 'SECRET', 'TOP_SECRET']),
    cloudServiceModel: faker.helpers.arrayElement(['IAAS', 'PAAS', 'SAAS']),
    environmentsInScope: faker.helpers.arrayElements(
      [
        'ADMINISTATION_ENVIRONMENT',
        'CLOUD_PRODUCTION_COMMON_CONTROLS',
        'CLOUD_PRODUCTION_SERVICE_SPECIFIC',
        'TEST_DEVELOPMENT',
        'PRODUCTION',
      ],
      { min: 1, max: 3 }
    ),
  }
}

export const updateISMScopingIntroductionFakeData = (): UpdateISMScopingIntroductionSchemaType => {
  return {
    id: faker.string.uuid(),
    assessmentBoundary: faker.lorem.sentence(20),
    dataClassification: faker.helpers.arrayElement(['NC', 'OFFICIAL_SENSITIVE', 'PROTECTED', 'SECRET', 'TOP_SECRET']),
    cloudServiceModel: faker.helpers.arrayElement(['IAAS', 'PAAS', 'SAAS']),
    environmentsInScope: faker.helpers.arrayElements(
      [
        'ADMINISTATION_ENVIRONMENT',
        'CLOUD_PRODUCTION_COMMON_CONTROLS',
        'CLOUD_PRODUCTION_SERVICE_SPECIFIC',
        'TEST_DEVELOPMENT',
        'PRODUCTION',
      ],
      { min: 1, max: 3 }
    ),
  }
}

export const createAssessedMerchantFakeData = (): AssessedMerchantSchemaType => {
  return {
    companyName: faker.company.name(),
    mailingAddress: faker.location.streetAddress(),
    companyWebsite: faker.internet.url(),
    doingBusinessAs: faker.company.name(),
    contactName: faker.person.fullName(),
    contactTitle: faker.person.jobTitle(),
    contactPhoneNumber: faker.phone.number(),
    contactEmail: faker.internet.email(),
  }
}

export const generateMeetingMinutesUsingAIFakeData = (): GenerateMeetingMinutesUsingAISchemaType => {
  return {
    notes: `Discussed quarterly risk assessment - identified three high-priority cybersecurity risks: unauthorized database access, insufficient encryption, and missing MFA. GDPR compliance at 90%, need to address gaps by Q2. New Information Security Policy requires all employees to complete security training by March 31st. Approved new Risk Management Committee to meet monthly. Action items: IT to implement MFA within 30 days, Compliance to update data retention policy by Feb 15th. Three vendors need security assessments completed by Q1 end. Audit found two non-conformities in access control and incident response - IT Security to address within 45 days. Scheduled Business Continuity tabletop exercise for Q2. New data protection regulations in 6 months - Legal to conduct gap analysis.`,
  }
}

export const createPciDssTaskFakeData = (): CreatePciDssTaskSchemaType => {
  return {
    controlId: '',
    domain: faker.lorem.words(3),
    requirementTitle: faker.lorem.words(3),
    taskTitle: faker.lorem.words(3),
    requirementText: faker.lorem.sentence(),
    clientGuidance: faker.lorem.sentence(),
    evidenceRequired: faker.lorem.sentence(),
    testingMethod: faker.lorem.sentence(),
    applicabilityStatus: faker.helpers.arrayElement(['Applicable', 'Not Applicable', 'Partial']),
    applicabilityScope: faker.lorem.words(4),
    responsibleRole: faker.person.jobTitle(),
    cadenceType: faker.helpers.arrayElement(['Monthly', 'Quarterly', 'Annually']),
    cadencePciStandard: faker.helpers.arrayElement(['PCI DSS v4.0', 'PCI DSS v3.2.1']),
    cadenceGovernanceReviewType: faker.lorem.words(2),
    cadenceGovernanceReviewIntervalMonths: faker.number.int({ min: 1, max: 12 }),
    calendarBucket: faker.lorem.word(),
    recuurrenceIntervalDays: faker.number.int({ min: 1, max: 90 }),
    recuurrenceInstancesPerYear: faker.number.int({ min: 1, max: 12 }),
    triggerType: faker.helpers.arrayElement(['Manual', 'Automatic', 'Event-Based']),
    triggerEvent: faker.lorem.words(2),
    active: faker.datatype.boolean(),
  }
}

export const createPciDssEvidencePackTaskFakeData = (): CreatePciDssEvidencePackTaskSchemaType => {
  return {
    ref: `01-${faker.string.alpha({ length: 3, casing: 'upper' })}-0${faker.number.int({ min: 1, max: 9 })}`,
    packName: faker.lorem.words(4),
    whatWeNeed: faker.lorem.sentence(),
    whyItMatters: faker.lorem.sentence(),
    goodResponse: faker.lorem.sentence(),
    priority: faker.helpers.arrayElement([
      PciEvidencePackPriority.HIGH,
      PciEvidencePackPriority.MEDIUM,
      PciEvidencePackPriority.LOW,
    ]),
    appliesTo: faker.lorem.sentence(),
    suggestedTeam: faker.helpers.arrayElement(['Network / Cloud Security', 'Security Operations', 'Compliance']),
    estEffort: faker.helpers.arrayElement([
      PciEvidencePackEffort.LOW,
      PciEvidencePackEffort.MODERATE,
      PciEvidencePackEffort.HIGH,
    ]),
    supported: true,
    unsupportedReason: '',
    controlIds: [],
    active: true,
  }
}

export const updatePciDssEvidencePackTaskFakeData = (): UpdatePciDssEvidencePackTaskSchemaType => {
  return {
    id: faker.string.uuid(),
    ...createPciDssEvidencePackTaskFakeData(),
  }
}

export const updatePciDssTaskFakeData = (): UpdatePciDssTaskSchemaType => {
  return {
    id: faker.string.uuid(),
    controlId: '',
    domain: faker.lorem.words(3),
    requirementTitle: faker.lorem.words(3),
    taskTitle: faker.lorem.words(3),
    requirementText: faker.lorem.sentence(),
    clientGuidance: faker.lorem.sentence(),
    evidenceRequired: faker.lorem.sentence(),
    testingMethod: faker.lorem.sentence(),
    applicabilityStatus: faker.helpers.arrayElement(['Applicable', 'Not Applicable', 'Partial']),
    applicabilityScope: faker.lorem.words(4),
    responsibleRole: faker.person.jobTitle(),
    cadenceType: faker.helpers.arrayElement(['Monthly', 'Quarterly', 'Annually']),
    cadencePciStandard: faker.helpers.arrayElement(['PCI DSS v4.0', 'PCI DSS v3.2.1']),
    cadenceGovernanceReviewType: faker.lorem.words(2),
    cadenceGovernanceReviewIntervalMonths: faker.number.int({ min: 1, max: 12 }),
    calendarBucket: faker.lorem.word(),
    recuurrenceIntervalDays: faker.number.int({ min: 1, max: 90 }),
    recuurrenceInstancesPerYear: faker.number.int({ min: 1, max: 12 }),
    triggerType: faker.helpers.arrayElement(['Manual', 'Automatic', 'Event-Based']),
    triggerEvent: faker.lorem.words(2),
    active: faker.datatype.boolean(),
  }
}

// Generate a password that meets the requirements:
// At least 8 characters, at least one letter and one number
export const generateValidPassword = () => {
  let password = ''
  // Updated: Now requires at least one letter, one digit, and one special character, min length 8
  const pattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/
  while (true) {
    // Generate with guaranteed 10 length and using all desired chars
    password = faker.internet.password({
      length: 10,
      memorable: false,
      pattern: /[A-Za-z0-9!@#$%^&*]/,
    })
    if (pattern.test(password)) break
  }
  return password
}

export const createCTRAProgramsFakeData = (): CreateProgramsSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    clientName: faker.company.name(),
    startDate: faker.date.past().toISOString(),
    endDate: faker.date.future().toISOString(),
    active: true,
  }
}

export const updateCTRAProgramsFakeData = (): UpdateProgramsSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    clientName: faker.company.name(),
    startDate: faker.date.past().toISOString(),
    endDate: faker.date.future().toISOString(),
    active: true,
  }
}

export const createCTRAZoneFakeData = (): CreateZoneSchemaType => {
  return {
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const updateCTRAZoneFakeData = (): UpdateZoneSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(3),
    description: faker.lorem.sentence(),
    active: true,
  }
}

export const createCTRAThreatScenarioCatalogFakeData = (): CreateThreatScenarioCatalogSchemaType => {
  return {
    name: faker.lorem.words(4),
    description: faker.lorem.sentence(),
  }
}

export const updateCTRAThreatScenarioCatalogFakeData = (): UpdateThreatScenarioCatalogSchemaType => {
  return {
    id: faker.string.uuid(),
    name: faker.lorem.words(4),
    description: faker.lorem.sentence(),
  }
}
